import type { ComponentType } from 'react';
import {
  AlertTriangle,
  BadgePercent,
  BookOpen,
  ClipboardList,
  Coins,
  Contact,
  FileUp,
  FileDown,
  FolderTree,
  Heart,
  Images,
  MessageSquare,
  LayoutDashboard,
  LayoutGrid,
  ListTree,
  Megaphone,
  Package,
  Settings,
  Shapes,
  Sparkles,
  Share2,
  SlidersHorizontal,
  StickyNote,
  Store,
  User,
  UserCog,
  Users,
  Warehouse,
} from 'lucide-react';
import {
  EXTENSION_GATED_MODULE_KEYS,
  HIDDEN_TENANT_MODULE_KEYS,
  PRODUCT_FEATURES,
} from './productSurface';
import { brand } from '../lib/brand';

export interface TenantModuleDefinition {
  /** Stable key used in slot persistence and group.modules */
  key: string;
  /** URL segment under /app/ */
  routeId: string;
  path: string;
  icon: ComponentType<{ size?: number; className?: string }>;
  labelKey: string;
}

export interface TenantModuleGroup {
  id: string;
  labelKey: string;
  modules: string[];
}

/** All tenant toolbar modules — MISSION_CONTROL §4.1 + catalog preview + profile. */
export const TENANT_MODULE_DEFINITIONS: Record<string, TenantModuleDefinition> = {
  overview: {
    key: 'overview',
    routeId: 'overview',
    path: '/app/overview',
    icon: LayoutDashboard,
    labelKey: 'nav.overview',
  },
  announcements: {
    key: 'announcements',
    routeId: 'announcements',
    path: '/app/announcements',
    icon: Megaphone,
    labelKey: 'nav.announcements',
  },
  notes: {
    key: 'notes',
    routeId: 'notes',
    path: '/app/notes',
    icon: StickyNote,
    labelKey: 'nav.notes',
  },
  marketplace: {
    key: 'marketplace',
    routeId: 'marketplace',
    path: '/app/marketplace',
    icon: Store,
    labelKey: 'nav.marketplace',
  },
  publisher: {
    key: 'publisher',
    routeId: 'publisher',
    path: '/app/publisher',
    icon: Share2,
    labelKey: 'nav.publisher',
  },
  stock_alerts: {
    key: 'stock_alerts',
    routeId: 'stock-alerts',
    path: '/app/stock-alerts',
    icon: AlertTriangle,
    labelKey: 'nav.stockAlerts',
  },
  crm: {
    key: 'crm',
    routeId: 'crm',
    path: '/app/crm',
    icon: Users,
    labelKey: 'nav.crm',
  },
  orders: {
    key: 'orders',
    routeId: 'orders',
    path: '/app/orders',
    icon: ClipboardList,
    labelKey: 'nav.orders',
  },
  products: {
    key: 'products',
    routeId: 'products',
    path: '/app/products',
    icon: Package,
    labelKey: 'nav.products',
  },
  galleries: {
    key: 'galleries',
    routeId: 'galleries',
    path: '/app/galleries',
    icon: Images,
    labelKey: 'nav.galleries',
  },
  album_reviews: {
    key: 'album_reviews',
    routeId: 'reviews',
    path: '/app/reviews',
    icon: MessageSquare,
    labelKey: 'nav.albumReviews',
  },
  album_published_reviews: {
    key: 'album_published_reviews',
    routeId: 'published-reviews',
    path: '/app/published-reviews',
    icon: MessageSquare,
    labelKey: 'nav.albumPublishedReviews',
  },
  album_requests: {
    key: 'album_requests',
    routeId: 'requests',
    path: '/app/requests',
    icon: Heart,
    labelKey: 'nav.albumRequests',
  },
  album_books: {
    key: 'album_books',
    routeId: 'books',
    path: '/app/books',
    icon: BookOpen,
    labelKey: 'nav.albumBooks',
  },
  categories: {
    key: 'categories',
    routeId: 'categories',
    path: '/app/categories',
    icon: FolderTree,
    labelKey: 'nav.categories',
  },
  attributes: {
    key: 'attributes',
    routeId: 'attributes',
    path: '/app/attributes',
    icon: SlidersHorizontal,
    labelKey: 'nav.attributes',
  },
  optionSets: {
    key: 'optionSets',
    routeId: 'option-sets',
    path: '/app/option-sets',
    icon: ListTree,
    labelKey: 'nav.optionSets',
  },
  variant_templates: {
    key: 'variant_templates',
    routeId: 'variant-templates',
    path: '/app/variant-templates',
    icon: Shapes,
    labelKey: 'products.variants.templatesTitle',
  },
  addon_templates: {
    key: 'addon_templates',
    routeId: 'addon-templates',
    path: '/app/addon-templates',
    icon: Sparkles,
    labelKey: 'products.addons.templatesTitle',
  },
  subcategories: {
    key: 'subcategories',
    routeId: 'subcategories',
    path: '/app/subcategories',
    icon: ListTree,
    labelKey: 'nav.subcategories',
  },
  import: {
    key: 'import',
    routeId: 'import',
    path: '/app/import',
    icon: FileUp,
    labelKey: 'nav.import',
  },
  import_media: {
    key: 'import_media',
    routeId: 'import-media',
    path: '/app/import-media',
    icon: Images,
    labelKey: 'nav.importMedia',
  },
  export: {
    key: 'export',
    routeId: 'export',
    path: '/app/export',
    icon: FileDown,
    labelKey: 'nav.export',
  },
  albums: {
    key: 'albums',
    routeId: 'albums',
    path: '/app/albums',
    icon: BookOpen,
    labelKey: 'nav.albums',
  },
  visiting_card: {
    key: 'visiting_card',
    routeId: 'visiting-card',
    path: '/app/visiting-card',
    icon: Contact,
    labelKey: 'nav.visitingCard',
  },
  prices: {
    key: 'prices',
    routeId: 'prices',
    path: '/app/prices',
    icon: Coins,
    labelKey: 'nav.prices',
  },
  promotions: {
    key: 'promotions',
    routeId: 'promotions',
    path: '/app/promotions',
    icon: BadgePercent,
    labelKey: 'nav.promotions',
  },
  stock: {
    key: 'stock',
    routeId: 'stock',
    path: '/app/stock',
    icon: Warehouse,
    labelKey: 'nav.stock',
  },
  catalog: {
    key: 'catalog',
    routeId: 'catalog',
    path: '/app/catalog',
    icon: LayoutGrid,
    labelKey: 'nav.catalog',
  },
  settings: {
    key: 'settings',
    routeId: 'settings',
    path: '/app/settings',
    icon: Settings,
    labelKey: 'nav.settings',
  },
  staff: {
    key: 'staff',
    routeId: 'staff',
    path: '/app/staff',
    icon: UserCog,
    labelKey: 'nav.staff',
  },
  profile: {
    key: 'profile',
    routeId: 'profile',
    path: '/app/profile',
    icon: User,
    labelKey: 'nav.profile',
  },
};

/** Default slot groups — FLPBox-style bar (5 slots). */
export const DEFAULT_TENANT_MODULE_GROUPS: TenantModuleGroup[] = [
  {
    id: 'overview',
    labelKey: 'nav.group.overview',
    modules: ['overview', 'announcements', 'notes', 'publisher'],
  },
  {
    id: 'crm',
    labelKey: 'nav.group.orders',
    modules: ['orders', 'marketplace'],
  },
  {
    id: 'catalog',
    labelKey: 'nav.group.catalog',
    modules: ['products', 'categories', 'attributes', 'optionSets', 'variant_templates', 'addon_templates', 'visiting_card', 'import', 'import_media', 'export'],
  },
  {
    id: 'storefront',
    labelKey: 'nav.group.storefront',
    modules: ['catalog', 'albums', 'prices', 'promotions'],
  },
  {
    id: 'system',
    labelKey: 'nav.group.system',
    modules: ['settings', 'staff', 'profile'],
  },
];

/** Nav groups with optional modules restored when product flags are on. */
export function effectiveTenantModuleGroups(
  groups: TenantModuleGroup[] = brand.tenantModuleGroups ?? DEFAULT_TENANT_MODULE_GROUPS,
): TenantModuleGroup[] {
  return groups
    .map((group) => {
      const modules = [...group.modules];
      if (group.id === 'crm' && PRODUCT_FEATURES.registeredClients && !modules.includes('crm')) {
        modules.unshift('crm');
      }
      if (group.id === 'storefront' && PRODUCT_FEATURES.stockManagement) {
        if (!modules.includes('stock')) modules.push('stock');
        if (!modules.includes('stock_alerts')) modules.push('stock_alerts');
      }
      return {
        ...group,
        // Extension-gated keys stay in group.modules; TenantNavRow hides them until enabled.
        modules: modules.filter(
          (key) =>
            EXTENSION_GATED_MODULE_KEYS.has(key) || !HIDDEN_TENANT_MODULE_KEYS.has(key),
        ),
      };
    })
    .filter((group) => group.modules.length > 0);
}

const slotsStorageKey = (tenantKey: string | undefined) =>
  `catalogofworks_tenant_nav_slots_${tenantKey || 'default'}`;

function parseSlots(raw: string | null): Record<string, string> | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as Record<string, string>;
  } catch {
    return null;
  }
}

function serializeSlotsByGroups(
  groups: TenantModuleGroup[],
  slots: Record<string, string>,
): string {
  const ordered: Record<string, string> = {};
  for (const g of groups) {
    ordered[g.id] = slots[g.id] || g.modules[0];
  }
  return JSON.stringify(ordered);
}

export function persistTenantModuleSlots(
  groups: TenantModuleGroup[],
  tenantKey: string | undefined,
  slots: Record<string, string>,
): void {
  const currentPayload = serializeSlotsByGroups(groups, slots);
  const defaultPayload = serializeSlotsByGroups(
    groups,
    groups.reduce<Record<string, string>>((acc, group) => {
      acc[group.id] = group.modules[0];
      return acc;
    }, {}),
  );

  if (currentPayload === defaultPayload) {
    localStorage.removeItem(slotsStorageKey(tenantKey));
    return;
  }

  localStorage.setItem(slotsStorageKey(tenantKey), currentPayload);
}

/** Merge persisted picks with group definitions; drop invalid module keys. */
export function resolveTenantModuleSlots(
  groups: TenantModuleGroup[],
  tenantKey: string | undefined,
  previous: Record<string, string> | null,
): Record<string, string> {
  const stored = parseSlots(localStorage.getItem(slotsStorageKey(tenantKey))) || {};

  const out: Record<string, string> = {};
  for (const g of groups) {
    const first = g.modules[0];
    const prevPick = previous?.[g.id];
    const storedPick = stored[g.id];
    const pick =
      (storedPick && g.modules.includes(storedPick) ? storedPick : null) ||
      (prevPick && g.modules.includes(prevPick) ? prevPick : null) ||
      first;
    out[g.id] = pick;
  }
  return out;
}

/** When route changes, only one slot may show that module as its face (copies stay independent). */
export function syncSlotsToRoute(
  groups: TenantModuleGroup[],
  routeId: string,
  slots: Record<string, string>,
  homes: Record<string, string> = {},
  preferredGroupId: string | null = null,
): Record<string, string> {
  if (!routeId) return slots;

  let matchingKey: string | null = null;
  const containing: string[] = [];
  for (const group of groups) {
    for (const moduleKey of group.modules) {
      const mod = TENANT_MODULE_DEFINITIONS[moduleKey];
      if (mod?.routeId === routeId) {
        matchingKey = moduleKey;
        containing.push(group.id);
        break;
      }
    }
  }
  if (!matchingKey || containing.length === 0) return slots;

  const showing = containing.filter((id) => slots[id] === matchingKey);
  const ownerId =
    (preferredGroupId && containing.includes(preferredGroupId) ? preferredGroupId : null)
    || (showing.length === 1 ? showing[0] : null)
    || (homes[matchingKey] && containing.includes(homes[matchingKey]) ? homes[matchingKey] : null)
    || containing[0];

  let changed = false;
  const next = { ...slots };
  if (next[ownerId] !== matchingKey) {
    next[ownerId] = matchingKey;
    changed = true;
  }

  for (const group of groups) {
    if (!containing.includes(group.id) || group.id === ownerId) continue;
    if (next[group.id] !== matchingKey) continue;
    const fallback = group.modules.find((key) => key !== matchingKey) || group.modules[0];
    if (fallback && next[group.id] !== fallback) {
      next[group.id] = fallback;
      changed = true;
    }
  }

  return changed ? next : slots;
}

